import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import postCompleteStepPayment from '../functions/API/postCompleteStepPayment.js'
import { toast } from '../lib/toast.js'
import { CheckCircle2, XCircle, ArrowLeft } from 'lucide-react'

export default function OnboardingPaymentResult() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [processing, setProcessing] = useState(true)
  const [success, setSuccess] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    completePayment()
  }, [])

  async function completePayment() {
    const stepId = searchParams.get('step_id')
    const transactionId = searchParams.get('transaction_id') || searchParams.get('transactionId') || 'unknown'
    const resultCode = searchParams.get('resultCode') || searchParams.get('result_code')

    if (!stepId) {
      setMessage('Missing payment information')
      setProcessing(false)
      return
    }

    // Determine status from AllPay result code
    let status = 'failed'
    if (resultCode === '0' || resultCode === 'success') {
      status = 'success'
    } else if (resultCode === '-1' || resultCode === 'cancelled') {
      status = 'cancelled'
    }

    const response = await postCompleteStepPayment({
      task_instance_step_id: parseInt(stepId),
      transaction_id: transactionId,
      status,
    })

    if (response.response?.ok) {
      if (status === 'success') {
        setSuccess(true)
        setMessage('Your payment was successful')
      } else if (status === 'cancelled') {
        setSuccess(false)
        setMessage('The payment was cancelled')
      } else {
        setSuccess(false)
        setMessage('The payment was not successful')
      }
    } else {
      setSuccess(false)
      setMessage(response.data?.message || 'Failed to process payment result')
    }

    setProcessing(false)
  }

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">Processing payment...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200 text-center">
          {success ? (
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
          ) : (
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          )}

          <h2 className={`text-xl font-semibold ${success ? 'text-gray-900' : 'text-gray-900'}`}>
            {success ? 'Payment Successful' : 'Payment Failed'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{message}</p>

          {success && (
            <p className="mt-3 text-xs text-gray-400">
              It can take up to one week from the date you make your payment for it to show on your account.
            </p>
          )}
        </div>

        <button
          onClick={() => navigate('/onboarding')}
          className="mt-6 w-full flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-3.5 text-sm font-semibold text-white hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to onboarding
        </button>
      </div>
    </div>
  )
}
